import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/services/firebase';
import { PRIVACY_CONFIG, getPrivacyCompliantValue } from '@/config/privacy';
import useAuthStore from '@/stores/authStore';
import type { ConsentData } from '@/components/compliance/ConsentFlow';

export const getClientIP = async (): Promise<string> => {
  if (!PRIVACY_CONFIG.COLLECT_IP_ADDRESSES) {
    return getPrivacyCompliantValue('', 'ip');
  }
  
  try {
    // For production, you might want a more reliable service
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return getPrivacyCompliantValue(data.ip, 'ip');
  } catch (error) {
    console.warn('Failed to get client IP:', error);
    return getPrivacyCompliantValue('unknown', 'ip');
  }
};

export const getDeviceFingerprint = (): string => {
  if (!PRIVACY_CONFIG.COLLECT_DEVICE_FINGERPRINTS) {
    return getPrivacyCompliantValue('', 'fingerprint');
  }
  
  try {
    // Create a simple device fingerprint
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint', 2, 2);
    }
    
    const fingerprint = {
      userAgent: getPrivacyCompliantValue(navigator.userAgent, 'userAgent'),
      language: navigator.language,
      platform: navigator.platform,
      screen: `${screen.width}x${screen.height}x${screen.colorDepth}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      canvas: canvas.toDataURL(),
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      hardwareConcurrency: navigator.hardwareConcurrency || 0,
      maxTouchPoints: navigator.maxTouchPoints || 0,
    };
    
    return btoa(JSON.stringify(fingerprint));
  } catch (error) {
    console.warn('Failed to generate device fingerprint:', error);
    return btoa(JSON.stringify({
      userAgent: getPrivacyCompliantValue(navigator.userAgent, 'userAgent'),
      timestamp: Date.now()
    }));
  }
};

export const recordConsent = async (consentData: ConsentData): Promise<string> => {
  try {
    const { user, organization } = useAuthStore.getState();
    
    const consentRecord = {
      ...consentData,
      userId: user?.id || 'anonymous',
      organizationId: organization?.id || 'unknown',
      formType: 'electronic_consent',
      createdAt: serverTimestamp(),
      version: '1.0', // For tracking consent version changes
    };

    const docRef = await addDoc(collection(db, 'electronicConsent'), consentRecord);
    console.log('Consent recorded with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Failed to record consent:', error);
    throw new Error('Failed to record electronic consent');
  }
};

export const hashData = async (data: string): Promise<string> => {
  try {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (error) {
    console.error('Failed to hash data:', error);
    throw new Error('Failed to create cryptographic hash');
  }
};

export const createCryptographicSeal = async (
  documentHash: string, 
  signatureHash: string, 
  timestamp?: Date
): Promise<string> => {
  try {
    const sealTimestamp = timestamp || new Date();
    const sealData = `${documentHash}:${signatureHash}:${sealTimestamp.toISOString()}`;
    return await hashData(sealData);
  } catch (error) {
    console.error('Failed to create cryptographic seal:', error);
    throw new Error('Failed to create document seal');
  }
};

export const validateDocumentIntegrity = async (
  originalHash: string,
  currentDocument: any
): Promise<boolean> => {
  try {
    const currentHash = await hashData(JSON.stringify(currentDocument));
    return originalHash === currentHash;
  } catch (error) {
    console.error('Failed to validate document integrity:', error);
    return false;
  }
};

export const getGeolocation = (): Promise<{ lat: number; lng: number } | null> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      () => {
        resolve(null);
      },
      {
        timeout: 10000,
        enableHighAccuracy: false,
        maximumAge: 300000 // 5 minutes
      }
    );
  });
};